import React, { useEffect, useState } from 'react';
import { hrmsListOnboarding, hrmsGetOnboarding, hrmsAdminDownloadDoc, hrmsUpdateOnboardingAdmin } from '../../lib/hrmsApi';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { CheckCircle2, Circle, Download, Video } from 'lucide-react';

const OnboardingAdminPage = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');

  const load = async () => setRows(await hrmsListOnboarding().catch(() => []));
  useEffect(() => { load(); }, []);

  const openOne = async (id) => {
    const ob = await hrmsGetOnboarding(id);
    setActive(ob);
    setVideoUrl(ob.welcome_video_url || '');
  };

  const download = async (doc_id, filename, mime) => {
    try {
      const res = await hrmsAdminDownloadDoc(active.id, doc_id);
      const bin = atob(res.content_b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: res.mime_type });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = res.filename;
      link.click();
    } catch { toast({ title: 'Download failed' }); }
  };

  const saveVideo = async () => {
    await hrmsUpdateOnboardingAdmin(active.id, { welcome_video_url: videoUrl });
    toast({ title: 'Welcome video updated' });
    openOne(active.id);
  };

  const avg = rows.length ? Math.round(rows.reduce((s, r) => s + (r.progress || 0), 0) / rows.length) : 0;
  const completed = rows.filter((r) => r.status === 'completed').length;

  return (
    <div data-testid="onboarding-admin-page">
      <h1 className="text-2xl font-extrabold">Onboarding tracker</h1>
      <p className="text-sm text-slate-500 mt-1">Live view of every new joiner's checklist progress and uploaded documents.</p>

      <div className="mt-6 grid grid-cols-3 gap-3">
        <div className="rounded-2xl bg-white border p-4"><div className="text-[11px] uppercase text-slate-500">In progress</div><div className="text-2xl font-extrabold">{rows.length - completed}</div></div>
        <div className="rounded-2xl bg-white border p-4"><div className="text-[11px] uppercase text-slate-500">Completed</div><div className="text-2xl font-extrabold text-emerald-600">{completed}</div></div>
        <div className="rounded-2xl bg-white border p-4"><div className="text-[11px] uppercase text-slate-500">Avg progress</div><div className="text-2xl font-extrabold text-orange-600">{avg}%</div></div>
      </div>

      <div className="mt-6 rounded-2xl border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left px-4 py-3">Employee</th>
            <th className="text-left px-4 py-3">Progress</th>
            <th className="text-left px-4 py-3">Docs uploaded</th>
            <th className="text-left px-4 py-3">Status</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="text-center text-slate-500 py-10">No onboarding records yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t cursor-pointer hover:bg-slate-50" onClick={() => openOne(r.id)} data-testid={`onboarding-row-${r.employee_id}`}>
                <td className="px-4 py-3 font-medium">{r.employee_name}<div className="text-xs text-slate-500">{r.employee_id}</div></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-32 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-orange-500" style={{ width: `${r.progress || 0}%` }} /></div>
                    <div className="text-xs font-mono">{r.progress || 0}%</div>
                  </div>
                </td>
                <td className="px-4 py-3">{r.documents?.length || 0}</td>
                <td className="px-4 py-3"><Badge className={r.status === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'}>{r.status.replace('_',' ')}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!active} onOpenChange={(v) => { if (!v) setActive(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {active && (
            <>
              <DialogTitle>{active.employee_name} · {active.employee_id}</DialogTitle>
              <div className="mt-3">
                <Label>Welcome video URL (embed link)</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://www.youtube.com/embed/..." />
                  <Button onClick={saveVideo} className="bg-orange-500 hover:bg-orange-600"><Video className="h-4 w-4 mr-1" /> Save</Button>
                </div>
              </div>

              <div className="mt-5">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Checklist ({active.tasks?.filter(t => t.completed).length}/{active.tasks?.length})</div>
                <div className="space-y-2">
                  {(active.tasks || []).map((t) => (
                    <div key={t.id} className="flex items-start gap-3 rounded-lg border border-slate-200 p-2 text-sm">
                      {t.completed ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <Circle className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />}
                      <div><div className={t.completed ? 'text-emerald-800' : 'text-slate-900'}>{t.title}</div><div className="text-xs text-slate-500">{t.category}</div></div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Uploaded documents ({active.documents?.length || 0})</div>
                <div className="space-y-1">
                  {(active.documents || []).map((d) => (
                    <button key={d.id} onClick={() => download(d.id, d.filename, d.mime_type)} className="w-full flex items-center gap-2 rounded-lg border border-slate-200 hover:border-orange-400 p-2 text-sm text-left">
                      <Download className="h-4 w-4 text-slate-500" /> <span className="font-semibold">{d.name}</span> <span className="text-xs text-slate-500">— {d.filename} ({Math.round((d.size || 0) / 1024)} KB)</span>
                    </button>
                  ))}
                  {(!active.documents || active.documents.length === 0) && <div className="text-xs text-slate-500">No documents uploaded yet.</div>}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingAdminPage;
